import { IconButton, Paper, Tooltip } from '@material-ui/core';
import ArrowBackIcon from '@material-ui/icons/ArrowBack';
import { makeStyles } from '@material-ui/styles';
import { AxiosError } from 'axios';
import { useSnackbar } from 'notistack';
import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import {
  Link,
  Redirect,
  Switch,
  useLocation,
  useParams,
} from 'react-router-dom';
import mentorsAPI, { Mentor, MentorStatus } from '../../api/mentors';
import { VentureId } from '../../api/ventures/types/Venture';
import {
  Button,
  ConfirmButton,
  MentorInviteModal,
  MentorManagementRequestButton,
  PageLoader,
  RouteACL,
  SnackMessage,
  WriteReviewButton,
} from '../../components/common';
import DeleteButton from '../../components/common/delete-modal';
import EmptyDetails from '../../components/common/empty-details';
import EnrichProfileModal from '../../components/common/enrich-profile-modal';
import BaseLayout from '../../components/layout/base-layout';
import { Header } from '../../components/layout/main-layout';
import PageTabs from '../../components/mentors/page-tabs';
import { MentorContext, MentorProvider } from '../../contexts/mentor-context';
import { useResourceBundles } from '../../contexts/resource-bundles-context';
import { UserContext } from '../../contexts/user-context';
import { getRoutePath, Pages } from '../../router/constants';
import { ProtectedRouteProps } from '../../router/type';
import MentorAdditionalInfoPage from './mentor-additional-info-page';
import MentorAssignmentsPage from './mentor-assignments-page';
import MentorDetailsPage from './mentor-details-page';
import MentorLogPage from './mentor-log-page';
import MentorNotesPage from './mentor-notes-page';
import MentorStatsPage from './mentor-stats-page';
import MentorVentureHistoryPage from './mentor-venture-history-page';

const useStyles = makeStyles({
  container: {
    flexGrow: 1,
    padding: 0,
  },
  emptyContainer: {
    flexGrow: 1,
    padding: 0,
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    textAlign: 'center',
  },
  content: {
    padding: 40,
  },
  requestLink: {
    minWidth: 187,
    whiteSpace: 'nowrap',
  },
  actionsContainer: {
    display: 'flex',
    columnGap: '16px',
  },
});

function MentorPage({ user }: ProtectedRouteProps) {
  const classes = useStyles();
  const { mentorId, ventureId } =
    useParams<{ mentorId: Mentor['id']; ventureId: VentureId }>();
  const location = useLocation<{
    prevPath?: string;
    openEnrichModal?: boolean;
  }>();
  const { rb } = useResourceBundles();

  const [prevPath] = useState(location.state?.prevPath);
  const {
    mentor,
    isMentorLoading,
    mentorSpecializations,
    isAccessDenied,
    loadMentor,
    loadMentorSpecializations,
    updateMentor,
  } = useContext(MentorContext);
  const { hasAccessToAction, identityid } = useContext(UserContext);
  const { enqueueSnackbar } = useSnackbar();
  const [sending, setSending] = useState(false);
  const [isOpenInviteModal, setIsOpenInviteModal] = useState(false);
  const [isOpenEnrichModal, setIsOpenEnrichModal] = useState(false);

  useEffect(() => {
    if (location.state?.openEnrichModal) {
      setIsOpenEnrichModal(true);
    }
  }, [location.state?.openEnrichModal]);

  const handleCloseEnrichModal = useCallback(() => {
    setIsOpenEnrichModal(false);
  }, []);

  const handleEnrichSuccess = useCallback(
    (updatedMentor: Mentor) => {
      updateMentor(updatedMentor);
      loadMentorSpecializations(mentorId);
    },
    [updateMentor, loadMentorSpecializations, mentorId],
  );

  const handleOpenMentorCard = useCallback(() => {
    window.open(getRoutePath(Pages.MENTOR_CARD, { mentorId }), '_blank');
  }, [mentorId]);

  const handleStatusChange = useCallback(
    async (status: MentorStatus) => {
      if (mentor) {
        const prevStatus = mentor.status;
        try {
          updateMentor({ ...mentor, status });
          await mentorsAPI.update(mentor.id, { ...mentor, status });
          enqueueSnackbar(
            `The ${rb(
              'mentor',
            )} status was successfully changed to '${status}'`,
            {
              variant: 'success',
            },
          );
        } catch (e: any) {
          const messageError = e.response?.data?.message;

          updateMentor({ ...mentor, status: prevStatus });
          enqueueSnackbar(
            `An error occurred while changing the ${rb(
              'mentor',
            )}'s status. Please, try again.`,
            {
              content: (key, message) =>
                SnackMessage({
                  key,
                  message,
                  variant: 'error',
                  additionalMessage: messageError,
                }),
              variant: 'error',
            },
          );
          console.error('error status change', e);
        }
      }
    },
    [mentor, updateMentor, enqueueSnackbar, rb],
  );

  const handleSendMentorUpdate = async (onClose: () => any) => {
    try {
      setSending(true);
      await mentorsAPI.sendMentorUpdateInvites([mentorId]);
      enqueueSnackbar('The profile update request was successfully sent', {
        variant: 'success',
      });
      setSending(false);
      onClose();
    } catch (e) {
      const messageError = (e as AxiosError).response?.data?.message;

      setSending(false);
      enqueueSnackbar(
        'An error occurred while sending profile update request. Please, try again.',
        {
          content: (key, message) =>
            SnackMessage({
              key,
              message,
              variant: 'error',
              additionalMessage: messageError,
            }),
          variant: 'error',
        },
      );
      throw e;
    }
  };

  // load mentor's info
  useEffect(() => {
    if (!mentor) {
      loadMentor(mentorId);
    }
  }, [mentorId, mentor, loadMentor]);

  // load mentor's specializations
  useEffect(() => {
    if (!mentorSpecializations) {
      loadMentorSpecializations(mentorId);
    }
  }, [mentorSpecializations, mentorId, loadMentorSpecializations]);

  const backLink = useMemo(() => {
    return (
      <Tooltip title={rb(`Back to ${rb('mentors-u')}`)}>
        <IconButton
          component={Link}
          to={prevPath || Pages.ACTIVE_MENTORS}
          data-testid='button-back-mentors'
        >
          <ArrowBackIcon />
        </IconButton>
      </Tooltip>
    );
  }, [prevPath, rb]);

  return (
    <BaseLayout user={user} fullHeight>
      {isMentorLoading ? (
        <PageLoader />
      ) : isAccessDenied ? (
        <EmptyDetails
          backLink={backLink}
          title='Access Denied'
          content='Unfortunately you do not have access to this page. Please contact program admin if you believe this is a mistake'
        />
      ) : !mentor ? (
        <EmptyDetails backLink={backLink} />
      ) : (
        <>
          <Header
            title={`${mentor?.firstName} ${mentor?.lastName}`}
            onStatusChange={handleStatusChange}
            backLink={backLink}
            isMergedActions
            actions={[
              {
                key: 'send-invite',
                label: 'Send Invite',
                hidden: !hasAccessToAction('userManagement.mentor'),
                component: (
                  <Button
                    onClick={() => setIsOpenInviteModal(true)}
                    variant='contained'
                    data-testid='button-invite-user'
                  >
                    Send Invite
                  </Button>
                ),
              },
              {
                key: 'mentor-card',
                label: `${rb('mentor-u')} Card`,
                hidden: !hasAccessToAction('mentor.details.view'),
                component: (
                  <Button
                    onClick={handleOpenMentorCard}
                    variant='contained'
                    data-testid='button-mentor-card'
                  >
                    {`${rb('mentor-u')} Card`}
                  </Button>
                ),
              },
              {
                key: 'send-update',
                label: 'Request Profile Update',
                hidden: !hasAccessToAction('mentor.details.update'),
                component: (
                  <ConfirmButton
                    title='Send Single Profile Update Request'
                    loading={sending}
                    body='Your are about to send a temporary link to allow participants to update their profile. The link can work only 1 time, and is available for 48 hours only.'
                    successProps={{
                      btnLabel: 'Ok',
                      onSuccess: handleSendMentorUpdate,
                    }}
                    cancelProps={{
                      btnLabel: 'Cancel',
                    }}
                    buttonRenderer={({ onClick }) => (
                      <Button
                        className={classes.requestLink}
                        onClick={onClick}
                        variant='contained'
                        data-testid='button-invite-mentor-update'
                      >
                        Request Profile Update
                      </Button>
                    )}
                  />
                ),
              },
              {
                key: 'enrich-profile',
                label: 'Enrich Profile',
                hidden: !hasAccessToAction('mentor.details.update'),
                component: (
                  <Button
                    onClick={() => setIsOpenEnrichModal(true)}
                    variant='contained'
                    data-testid='button-enrich-profile'
                  >
                    Enrich Profile
                  </Button>
                ),
              },
              {
                key: 'manage-mentor',
                label: `Manage ${rb('mentor-u')}`,
                hidden: !hasAccessToAction('mentor.details.manageMentor'),
                component: (
                  <MentorManagementRequestButton
                    title={`Manage ${mentor?.fullName}`}
                    mentorId={mentor?.id || null}
                    ventureId={ventureId}
                    founderId={identityid}
                    buttonText={`Manage ${rb('mentor-u')}`}
                    defaultType='REMOVE'
                    selectableType
                  />
                ),
              },
              {
                key: 'write-review',
                label: 'Write a review',
                hidden: !hasAccessToAction('mentor.details.writeReview'),
                component: (
                  <WriteReviewButton
                    mentorName={mentor?.fullName}
                    ventureId={ventureId}
                    mentorId={mentorId}
                  />
                ),
              },
              {
                key: 'delete',
                label: 'Delete',
                hidden: !hasAccessToAction('mentor.delete'),
                component: <DeleteButton type='mentor' id={mentorId} />,
              },
            ]}
          />
          <Paper className={classes.container} elevation={1}>
            <PageTabs mentorId={mentorId} ventureId={ventureId} />
            <div className={classes.content}>
              <Switch>
                <RouteACL
                  exact
                  path={Pages.MY_MENTOR_DETAILS}
                  component={MentorDetailsPage}
                  rule='mentor.details.view'
                  redirectUrl={Pages.MENTORS}
                />
                <RouteACL
                  exact
                  path={Pages.MY_MENTOR_ADDITIONAL_INFO}
                  component={MentorAdditionalInfoPage}
                  rule={
                    identityid !== mentorId
                      ? 'mentor.additionalInfo.view'
                      : undefined
                  }
                  redirectUrl={Pages.MENTORS}
                />
                <RouteACL
                  exact
                  path={Pages.MENTOR_DETAILS}
                  component={MentorDetailsPage}
                  rule='mentor.details.view'
                  redirectUrl={Pages.ACTIVE_MENTORS}
                />
                <RouteACL
                  exact
                  path={Pages.MENTOR_ADDITIONAL_INFO}
                  component={MentorAdditionalInfoPage}
                  rule={
                    identityid !== mentorId
                      ? 'mentor.additionalInfo.view'
                      : undefined
                  }
                  redirectUrl={Pages.ACTIVE_MENTORS}
                />
                <RouteACL
                  exact
                  path={Pages.MENTOR_VENTURE_HISTORY}
                  component={MentorVentureHistoryPage}
                  rule='mentor.details.view'
                  redirectUrl={Pages.ACTIVE_MENTORS}
                />
                <RouteACL
                  exact
                  path={Pages.MENTOR_ASSIGNMENTS}
                  component={MentorAssignmentsPage}
                  rule='mentor.assignemnts.view'
                  redirectUrl={Pages.ACTIVE_MENTORS}
                />
                <RouteACL
                  exact
                  path={Pages.MENTOR_NOTES}
                  component={MentorNotesPage}
                  rule='mentor.notes.view'
                  redirectUrl={Pages.ACTIVE_MENTORS}
                />
                <RouteACL
                  exact
                  path={Pages.MENTOR_STATS}
                  component={MentorStatsPage}
                  rule='mentor.statistics'
                  redirectUrl={Pages.ACTIVE_MENTORS}
                />
                <RouteACL
                  exact
                  path={Pages.MENTOR_LOG}
                  component={MentorLogPage}
                  rule='mentor.logs.view'
                  redirectUrl={Pages.ACTIVE_MENTORS}
                />
                <Redirect to={Pages.MENTOR_DETAILS} />
              </Switch>
            </div>
          </Paper>
          {hasAccessToAction('userManagement.mentor') && (
            <MentorInviteModal
              open={isOpenInviteModal}
              mentorId={mentorId}
              onClose={() => setIsOpenInviteModal(false)}
            />
          )}
          <EnrichProfileModal
            open={isOpenEnrichModal}
            onClose={handleCloseEnrichModal}
            type='mentor'
            entityId={mentorId}
            onSuccess={handleEnrichSuccess}
          />
        </>
      )}
    </BaseLayout>
  );
}

function withMentorProvider<P>(Component: React.FunctionComponent<P>) {
  return (props: P) => (
    <MentorProvider>
      <Component {...props} />
    </MentorProvider>
  );
}

export default withMentorProvider(MentorPage);
